import { HttpClient } from '@angular/common/http';
import {
  NgxExcelModelColumnRules,
  NgxExcelModelColumnRule,
  NgxExcelSelectOption,
  NgxExcelHttpResponse,
  NgxExcelColumnType,
  NgxExcelUploadFile
} from '../models/models';
import { NgxExcelHelper, NgxExcelUploadService } from './ngx-excel-helper';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { isNull, isString, isNumber, isObject, isArray } from 'util';
import { utc } from 'moment';
import { snakeCase } from 'change-case';
import * as lodash from 'lodash';
import { INgxExcelDataSource } from './ngx-excel-data-source';

export abstract class NgxExcelService<T> extends INgxExcelDataSource<T> {

  protected abstract resourceUri: string;
  protected abstract resourceName: string;
  protected abstract rules: { [name in keyof T]: NgxExcelModelColumnRule<T> };
  protected additionalRules: { [name in keyof T]?: NgxExcelModelColumnRule<T> };
  protected metas: { [name: string]: any } = {}; // å·²è¢«ç¼å­çåæ°æ®

  constructor(
    protected httpClient: HttpClient,
    protected helper: NgxExcelHelper
  ) {
    super();
  }

  /**
   * è·å¾ææå­æ®µçæ¨¡åè§å
   */
  public getRules(): NgxExcelModelColumnRules<T> {
    this.rules = Object.assign({}, this.rules, this.additionalRules || {});
    Object.keys(this.rules).forEach((ruleKey) => {
      this.rules[ruleKey].name = this.rules[ruleKey].name || ruleKey;
    });
    return this.rules;
  }

  /**
   * è·å¾æå®å­æ®µçæ¨¡åè§åï¼æå¡ç©¿éï¼
   * @param name å­æ®µå
   */
  public getRule(name: string): NgxExcelModelColumnRule<T> {
    const rules = this.getRules();
    return rules[name] || null;
  }

  /**
   * è·å¾æ¨¡åä¸»é®å¼
   * @param model æ¨¡å
   */
  public getPrimaryKey(model: T): string {
    const rules = this.getRules();
    const rule = Object.entries<NgxExcelModelColumnRule<T>>(rules).find((entry) => entry[1].columnType === NgxExcelColumnType.PrimaryKey);
    return model[rule[0]] || '';
  }

  /**
   * åå»ºæ¨¡å
   * @param attributes å¾è½¬åçæ¨¡åå±æ§å¼
   * @param o æå¡å¨è¿åçå­æ®µåè¡¨
   */
  public createModel(attributes?: Partial<T>, o?: any): T {
    const model = o ? this.resolve(o) : this.getDefaultModel();
    return (attributes ? Object.assign(model, attributes) : model) as T;
  }

  /**
   * è·å¾æå¡å¨ååºæåä¸æ¬¡ååºçåæ°æ®
   */
  public getResponseMetas(): { [name: string]: any } {
    return this.metas;
  }

  /**
   * è·å¾æ¨¡ååè¡¨çèµæºååº
   * @param filters è¯·æ±åè¡¨ç­éåæ°
   * @param page é¡µç 
   * @param pageSize é¡µé¢é¿åº¦
   */
  public getList(filters?: { [name: string]: string | string[] }, page?: number, pageSize?: number): Observable<T[]> {
    const payload: { [name: string]: string | string[] } = Object.assign({}, filters || {});
    if (page && pageSize) {
      payload.page = `${page}`;
      payload.pageSize = `${pageSize}`;
    } else {
      payload.loadCollection = 'true';
    }

    const { resourceUri, resourceParams } = this.getResourceUri(null, payload);
    if (resourceUri.length === 0) {
      return this.sendResponseError({ message: 'è¯·æ±æå¡å¨å°åéè¯¯' });
    }

    return this.httpClient.get<NgxExcelHttpResponse>(resourceUri, { params: this.getQueryParams(resourceParams) }).pipe(
      map((res) => {
        this.metas = res.getMetas();
        return res.getCollection<T>(this.resourceName, (o) => this.resolve(o));
      }),
      catchError((e) => this.sendResponseError(e))
    );
  }

  /**
   * è·å¾æ¨¡åå®ä¾çèµæºååº
   * @param primaryKey æ¨¡åä¸»é®
   * @param filters è¯·æ±ç­éåæ°
   */
  public getModel(primaryKey: string, filters?: { [name: string]: string | string[] }): Observable<T> {
    const payload: { [name: string]: string | string[] } = Object.assign({}, filters || {});
    const { resourceUri, resourceParams } = this.getResourceUri(primaryKey, payload);
    if (resourceUri.length === 0) {
      return this.sendResponseError({ message: 'è¯·æ±æå¡å¨å°åéè¯¯' });
    }

    return this.httpClient.get<NgxExcelHttpResponse>(resourceUri, { params: this.getQueryParams(resourceParams) }).pipe(
      map((res) => {
        this.metas = res.getMetas();
        return res.getModel<T>((o) => this.resolve(o));
      }),
      catchError((e) => this.sendResponseError(e))
    );
  }

  /**
   * æ´æ°æåå»ºå®ä¾æ¨¡å
   * @param payload è¯·æ±åæ°ææ¨¡åå¯¹è±¡
   * @param primaryKey æ¨¡åä¸»é®(å¦æä¾ä¸»é®ä»£è¡¨æ´æ°æ¨¡å, å¦ååå»ºæ¨¡å)
   */
  public save(payload: { [name: string]: any }, primaryKey?: string): Observable<T> {
    if (primaryKey) { return this.update(payload, primaryKey); }
    const { resourceUri, resourceParams } = this.getResourceUri(null, payload);
    if (resourceUri.length === 0) {
      return this.sendResponseError({ message: 'è¯·æ±æå¡å¨å°åéè¯¯' });
    }
    return this.httpClient.post<NgxExcelHttpResponse>(resourceUri, this.resolveBody(resourceParams)).pipe(
      map((res) => {
        this.metas = res.getMetas();
        return res.getModel<T>((o) => this.resolve(o));
      }),
      catchError((e) => this.sendResponseError(e))
    );
  }

  /**
   * æ´æ°æ¨¡å
   * @param payload è¯·æ±åæ°ææ¨¡åå¯¹è±¡
   * @param primaryKey æ¨¡åä¸»é®
   */
  public update(payload: { [name: string]: any }, primaryKey: string): Observable<T> {
    const { resourceUri, resourceParams } = this.getResourceUri(primaryKey, payload);
    if (resourceUri.length === 0) {
      return this.sendResponseError({ message: 'è¯·æ±æå¡å¨å°åéè¯¯' });
    }
    return this.httpClient.patch<NgxExcelHttpResponse>(resourceUri, this.resolveBody(resourceParams)).pipe(
      map((res) => {
        this.metas = res.getMetas();
        return res.getModel<T>((o) => this.resolve(o));
      }),
      catchError((e) => this.sendResponseError(e))
    );
  }

  /**
   * å é¤æ¨¡åå®ä¾
   * @param model æ¨¡åææ¨¡åä¸»é®
   * @param payload å é¤æ¶å¯ä¼ éåæ°
   */
  public destroy(model: T | string, payload?: { [name: string]: string | string[] }): Observable<T> {
    const primaryKey = typeof (model) === 'string' ? model : this.getPrimaryKey(model);
    if (!primaryKey) {
      return this.sendResponseError({ message: 'è¯·æ±æå¡å¨å°åéè¯¯' });
    }
    const { resourceUri, resourceParams } = this.getResourceUri(primaryKey, payload);
    if (resourceUri.length === 0) {
      return this.sendResponseError({ message: 'è¯·æ±æå¡å¨å°åéè¯¯' });
    }

    return this.httpClient.delete<NgxExcelHttpResponse>(resourceUri, { params: this.getQueryParams(resourceParams) }).pipe(
      map((res) => {
        this.metas = res.getMetas();
        return res.getModel<T>((o) => this.resolve(o));
      }),
      catchError((e) => this.sendResponseError(e))
    );
  }

  /**
   * æ¹éå¢å 
   * @param payload æ¹éå¢å æ¶éè¦ä¼ éçåæ°
   */
  public batchSave(payload: { [name: string]: any }): Observable<T[]> {
    const { resourceUri, resourceParams } = this.getResourceUri(null, payload);
    if (resourceUri.length === 0) {
      return this.sendResponseError({ message: 'è¯·æ±æå¡å¨å°åéè¯¯' });
    }

    return this.httpClient.post<NgxExcelHttpResponse>(resourceUri, this.resolveBody(resourceParams)).pipe(
      map((res) => {
        this.metas = res.getMetas();
        return res.getCollection<T>(this.resourceName, (o) => this.resolve(o));
      }),
      catchError((e) => this.sendResponseError(e))
    );
  }

  /**
   * æ¹éæ´æ°
   * @param payload æ¹éæ´æ°æ¶éè¦ä¼ éçåæ°
   */
  public batchUpdate(payload: { [name: string]: any }): Observable<T[]> {
    const { resourceUri, resourceParams } = this.getResourceUri(null, payload);
    if (resourceUri.length === 0) {
      return this.sendResponseError({ message: 'è¯·æ±æå¡å¨å°åéè¯¯' });
    }

    return this.httpClient.patch<NgxExcelHttpResponse>(resourceUri, this.resolveBody(resourceParams)).pipe(
      map((res) => {
        this.metas = res.getMetas();
        return res.getCollection<T>(this.resourceName, (o) => this.resolve(o));
      }),
      catchError((e) => this.sendResponseError(e))
    );
  }

  /**
   * æ¹éå é¤
   * @param payload æ¹éåæ°æ¶éè¦ä¼ éçåæ°
   */
  public batchDestroy(payload?: { [name: string]: any }): Observable<T[]> {
    const { resourceUri, resourceParams } = this.getResourceUri(null, payload);
    if (resourceUri.length === 0) {
      return this.sendResponseError({ message: 'è¯·æ±æå¡å¨å°åéè¯¯' });
    }

    return this.httpClient.delete<NgxExcelHttpResponse>(resourceUri, { params: this.getQueryParams(resourceParams) }).pipe(
      map((res) => {
        this.metas = res.getMetas();
        return res.getCollection<T>(this.resourceName, (o) => this.resolve(o));
      }),
      catchError((e) => this.sendResponseError(e))
    );
  }

  /**
   * æ ¹æ®èªå®ä¹åæ°è·å¾æä»¶ä¸ä¼ æå¡å¯¹è±¡
   * @param payload èªå®ä¹åæ°
   */
  public getUploadService(payload: { [name: string]: string | Blob }): NgxExcelUploadService {
    return this.helper.getUploadService(payload);
  }

  /**
   * å¤çæå¡å¨ç¸å³éè¯¯
   * @param e éè¯¯ä¿¡æ¯
   */
  protected sendResponseError(e: any): Observable<never> {
    this.metas = {};
    return throwError(e);
  }

  /**
   * è·å¾å®éçèµæºURI
   * @param primaryKey æ¨¡åä¸»é®(ä¸ä¼ éè¿åèµæºéåURIå¦åè¿åèµæºè¯¦æURI)
   * @param resourceParams è¯·æ±åæ°
   */
  protected getResourceUri(
    primaryKey?: string,
    payload?: { [name: string]: any }
  ): { resourceUri: string, resourceParams: { [name: string]: any } } {
    let resourceUri = this.resourceUri;
    const resourceParams: { [name: string]: any } = {};

    Object.keys(payload || {}).forEach((paramKey) => {
      const paramValue = payload[paramKey];
      if (!isString(paramValue) && !isNumber(paramValue)) {
        resourceParams[paramKey] = paramValue;
      } else if (resourceUri.indexOf(`{${paramKey}}`) >= 0) {
        resourceUri = resourceUri.replace(`{${paramKey}}`, `${paramValue}`);
      } else if (resourceUri.indexOf(`{${snakeCase(paramKey)}}`) >= 0) {
        resourceUri = resourceUri.replace(`{${snakeCase(paramKey)}}`, `${paramValue}`);
      } else {
        resourceParams[paramKey] = paramValue;
      }
    });

    if (resourceUri.indexOf('{') >= 0) {
      resourceUri = '';
    } else if (primaryKey) {
      resourceUri = resourceUri.endsWith('/') ? `${resourceUri}${primaryKey}` : `${resourceUri}/${primaryKey}`;
    }

    return { resourceUri, resourceParams };
  }

  /**
   * å¹³éºæ¥è¯¢åæ°
   * @param params æ¥è¯¢åæ°
   */
  protected getQueryParams(payload: { [name: string]: any }): { [name: string]: string | string[] } {
    const queryParams = {};
    Object.keys(payload).forEach((paramKey) => {
      const paramValue = payload[paramKey];
      if (isArray(paramValue)) {
        paramKey = snakeCase(paramKey);
        paramValue.forEach((value, index) => queryParams[`${paramKey}[${index}]`] = `${value}`);
      } else if (isObject(paramValue)) {
        paramKey = snakeCase(paramKey);
        Object.keys(paramValue).forEach((objectKey) => queryParams[`${paramKey}[${objectKey}]`] = `${paramValue[objectKey]}`);
      } else {
        queryParams[snakeCase(paramKey)] = paramValue;
      }
    });
    return queryParams;
  }

  /**
   * æ ¹æ® name è·å¾éé¡¹åè¡¨
   * @param name æä¸¾æ è¯
   */
  protected getSelectOptions(name: string): NgxExcelSelectOption[] {
    return this.helper.getSelectOptions(name);
  }

  /**
   * å°æå¡å¨ååºè½¬åä¸ºå¯¹åºçæ¨¡å
   * @param o æå¡å¨è¿åçæ¶æ¯å¯¹è±¡
   */
  protected resolve(o: any): T {
    const model = {};
    const rules = this.getRules();
    Object.keys(rules).forEach((name) => {
      const rule = rules[name] as NgxExcelModelColumnRule<T>;
      model[name] = null;

      if (rule.resolveValue) {
        model[name] = rule.resolveValue(o, model);
        model[name] = model[name] || this.getDefaultAttributeValue(rule);
        return;
      }

      if (rule.resolveKey) {
        // resolveKey
        model[name] = lodash.get(o, rule.resolveKey(name), null);
      } else if (rule.prop) {
        // prop
        (Array.isArray(rule.prop) ? rule.prop : [rule.prop]).forEach((prop) => {
          const value = lodash.get(o, prop, null);
          if (!isNull(value)) { model[name] = value; }
        });
      } else {
        // name
        model[name] = lodash.get(o, name, null) || lodash.get(o, snakeCase(name), null);
      }

      if (isNull(model[name])) {
        model[name] = this.getDefaultAttributeValue(rule);
      }

      switch (rule.columnType) {
        case NgxExcelColumnType.PrimaryKey:
          model[name] = `${model[name]}`;
          break;
        case NgxExcelColumnType.ForeignKey:
          if (model[name] && rule.relativeService) {
            model[name] = rule.relativeService.createModel(null, model[name]);
          }
          break;
        case NgxExcelColumnType.MultiForeignKey:
          if (isArray(model[name]) && model[name].length > 0 && rule.relativeService) {
            model[name] = model[name].map((m: any) => rule.relativeService.createModel(null, m));
          } else {
            model[name] = [];
          }
          break;
        case NgxExcelColumnType.UploadFile:
          if (!model[name]) { break; }
          const uploadFile: NgxExcelUploadFile = {
            id: model[name].id || '',
            name: model[name].file_name || 'æªç¥',
            url: model[name].file_full_url || '',
            mimeType: model[name].mime_type || ''
          };
          model[name] = (uploadFile.id && uploadFile.url) ? uploadFile : null;
          break;
        case NgxExcelColumnType.MultiUploadFile:
          model[name] = model[name].filter(({ id, file_url }) => id && file_url).map(({ id, file_name, file_full_url, mime_type }) => ({
            id: id || '',
            name: file_name || 'æªç¥',
            url: file_full_url || '',
            mimeType: mime_type || ''
          }));
          break;
        case NgxExcelColumnType.Bool:
          if (rule.optional && (model[name] === null || model[name] === undefined)) {
            model[name] = null;
          } else {
            model[name] = !!model[name];
          }
          break;
        case NgxExcelColumnType.Array:
          model[name] = isArray(model[name]) ? model[name] : [];
          break;
        case NgxExcelColumnType.DateTime:
          if (model[name].length > 0) {
            const object = utc(model[name], 'YYYY-MM-DD HH:mm:ss');
            model[name] = object.isValid() ? object.format('YYYY-MM-DD HH:mm') : '';
          }
          break;
        case NgxExcelColumnType.DateTimeRange:
          if (model[name].length > 0) {
            const pos = model[name].indexOf(',');
            if (pos < 0) {
              model[name] = '';
            } else {
              const objectBegin = utc(model[name].substring(0, pos), 'YYYY-MM-DD HH:mm:ss');
              const stringBegin = objectBegin.isValid() ? objectBegin.format('YYYY-MM-DD HH:mm') : '';
              const objectEnd = utc(model[name].substring(pos + 1), 'YYYY-MM-DD HH:mm:ss');
              const stringEnd = objectEnd.isValid() ? objectEnd.format('YYYY-MM-DD HH:mm') : '';
              model[name] = stringBegin.length === 0 && stringEnd.length === 0 ? `${stringBegin},${stringEnd}` : '';
            }
          }
          break;
        case NgxExcelColumnType.Date:
          if (model[name].length > 0) {
            const object = utc(model[name], 'YYYY-MM-DD');
            model[name] = object.isValid() ? object.format('YYYY-MM-DD') : '';
          }
          break;
        case NgxExcelColumnType.DateRange:
          if (model[name].length > 0) {
            const pos = model[name].indexOf(',');
            if (pos < 0) {
              model[name] = '';
            } else {
              const objectBegin = utc(model[name].substring(0, pos), 'YYYY-MM-DD');
              const stringBegin = objectBegin.isValid() ? objectBegin.format('YYYY-MM-DD') : '';
              const objectEnd = utc(model[name].substring(pos + 1), 'YYYY-MM-DD');
              const stringEnd = objectEnd.isValid() ? objectEnd.format('YYYY-MM-DD') : '';
              model[name] = stringBegin.length === 0 && stringEnd.length === 0 ? `${stringBegin},${stringEnd}` : '';
            }
          }
          break;
        case NgxExcelColumnType.Time:
          if (model[name].length > 0) {
            const object = utc(model[name], 'HH:mm:ss');
            model[name] = object.isValid() ? object.format('HH:mm') : '';
          }
          break;
        case NgxExcelColumnType.SelectOption:
          if (!rule.selectOptions || isNull(model[name])) { break; }
          model[name] = rule.selectOptions.find((selectOption) => selectOption.value === `${model[name]}`) || null;
          break;
        case NgxExcelColumnType.TagsSelectOption:
        case NgxExcelColumnType.MultiSelectOption:
          if (!rule.selectOptions || model[name].length === 0) { break; }
          const range = model[name].map((n: string | number) => `${n}`);
          model[name] = rule.selectOptions.filter((selectOption) => range.indexOf(selectOption.value) >= 0);
          break;
      }
    });

    return model as T;
  }

  /**
   * è§£ææäº¤åæ°
   * @param payload éè¦æäº¤çæ¶æ¯ä½
   */
  protected resolveBody(payload: { [name: string]: any }): { [name: string]: any } {
    const body: { [name: string]: any } = {};
    const rules = this.getRules();
    Object.keys(payload).forEach((name) => {
      if (Object.keys(rules).indexOf(name) < 0) {
        body[snakeCase(name)] = payload[name];
        return;
      }

      const rule = rules[name] as NgxExcelModelColumnRule<T>;
      let bodyKey = rule.prop ? (Array.isArray(rule.prop) ? rule.prop[0] : rule.prop) : snakeCase(name);
      if (rule.columnType === NgxExcelColumnType.ForeignKey) {
        bodyKey += '_id';
      } else if (rule.columnType === NgxExcelColumnType.MultiForeignKey) {
        bodyKey += '_ids';
      }

      const bodyValue = payload[name];
      switch (rule.columnType) {
        case NgxExcelColumnType.ForeignKey:
          if (bodyValue && rule.relativeService) {
            body[bodyKey] = rule.relativeService.getPrimaryKey(bodyValue) || '';
          }
          break;
        case NgxExcelColumnType.MultiForeignKey:
          body[bodyKey] = (isArray(bodyValue) && rule.relativeService) ?
            bodyValue.map((value) => rule.relativeService.getPrimaryKey(value)) :
            [];
          break;
        case NgxExcelColumnType.SelectOption:
          body[bodyKey] = lodash.get(bodyValue, 'value', '');
          break;
        case NgxExcelColumnType.MultiSelectOption:
          body[bodyKey] = isArray(bodyValue) ? bodyValue.map((value) => lodash.get(value, 'value', '')) : [];
          break;
        case NgxExcelColumnType.TagsSelectOption:
          body[bodyKey] = isArray(bodyValue) ? bodyValue.map((value) => lodash.get(value, 'label', '')) : [];
          break;
        case NgxExcelColumnType.UploadFile:
          body[bodyKey] = lodash.get(bodyValue, 'url', '');
          break;
        case NgxExcelColumnType.MultiUploadFile:
          body[bodyKey] = isArray(bodyValue) ? bodyValue.map((value) => lodash.get(value, 'url', '')) : [];
          break;
        case NgxExcelColumnType.Bool:
          if (rule.optional && (bodyValue === null || bodyValue === undefined)) {
            body[bodyKey] = '';
          } else {
            body[bodyKey] = bodyValue ? 1 : 0;
          }
          break;
        case NgxExcelColumnType.Currency:
          body[bodyKey] = bodyValue ? parseFloat(bodyValue).toFixed(4) : '0.0000';
          break;
        case NgxExcelColumnType.DateTime:
        case NgxExcelColumnType.Date:
        case NgxExcelColumnType.Time:
          body[bodyKey] = '';
          if (!bodyValue) { break; }

          const sourceFormatter = rule.columnType === NgxExcelColumnType.DateTime ? 'YYYY-MM-DD HH:mm:ss' : (
            rule.columnType === NgxExcelColumnType.Date ? 'YYYY-MM-DD' : 'HH:mm'
          );
          const targetFormatter = rule.columnType === NgxExcelColumnType.DateTime ? 'YYYY-MM-DD HH:mm' : (
            rule.columnType === NgxExcelColumnType.Date ? 'YYYY-MM-DD' : 'HH:mm'
          );
          const object = utc(bodyValue, sourceFormatter);
          body[bodyKey] = object.isValid() ? object.format(targetFormatter) : '';
          break;
        case NgxExcelColumnType.DateTimeRange:
        case NgxExcelColumnType.DateRange:
          body[bodyKey] = '';
          if (!bodyValue) { break; }
          const pos = bodyValue.indexOf(',');
          if (pos < 0) { break; }
          const sourceRangeFormatter = rule.columnType === NgxExcelColumnType.DateTimeRange ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
          const targetRangeFormatter = rule.columnType === NgxExcelColumnType.DateTimeRange ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';
          const objectBegin = utc(bodyValue.substring(0, pos), sourceRangeFormatter);
          const stringBegin = objectBegin.isValid() ? objectBegin.format(targetRangeFormatter) : '';
          const objectEnd = utc(bodyValue.substring(pos + 1), sourceRangeFormatter);
          const stringEnd = objectEnd.isValid() ? objectEnd.format(targetRangeFormatter) : '';
          if (stringBegin.length === 0 && stringEnd.length === 0) {
            body[bodyKey] = '';
          } else {
            body[bodyKey] = `${stringBegin},${stringEnd}`;
          }
          break;
        default:
          body[bodyKey] = bodyValue || '';
      }
    });
    return body;
  }

  /**
   * è·å¾å±æ§çé»è®¤å¼
   * @param rule å±æ§è§å
   */
  protected getDefaultAttributeValue(rule: NgxExcelModelColumnRule<T>): any {
    if (rule.default) {
      return rule.default;
    }
    let value: any;
    switch (rule.columnType) {
      case NgxExcelColumnType.PrimaryKey:
      // case NgxExcelColumnType.ForeignKey:
      case NgxExcelColumnType.Text:
      case NgxExcelColumnType.TextNumber:
      case NgxExcelColumnType.MultilineText:
      case NgxExcelColumnType.DateTime:
      case NgxExcelColumnType.DateTimeRange:
      case NgxExcelColumnType.Date:
      case NgxExcelColumnType.DateRange:
      case NgxExcelColumnType.Time:
        value = '';
        break;
      case NgxExcelColumnType.Number:
        value = 0;
        break;
      case NgxExcelColumnType.Currency:
        value = '0.0000';
        break;
      case NgxExcelColumnType.Bool:
        value = rule.optional ? null : false;
        break;
      case NgxExcelColumnType.Array:
      case NgxExcelColumnType.TagsSelectOption:
      case NgxExcelColumnType.MultiSelectOption:
      case NgxExcelColumnType.MultiForeignKey:
      case NgxExcelColumnType.MultiUploadFile:
        value = [];
        break;
      default:
        value = null;
    }
    return value;
  }

  /**
   * è·å¾é»è®¤æ¨¡å
   */
  protected getDefaultModel(): T {
    const model = {};
    const rules = this.getRules();
    Object.keys(rules).forEach((name) => {
      model[name] = this.getDefaultAttributeValue(rules[name]);
    });
    return model as T;
  }

}
